import React from "react";
import { useTranslation } from "react-i18next";
import { User, ClipboardList, Pill, FlaskConical, Scan } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { YesNoRadio } from "./YesNoRadio";
import { CLINIC_COLORS } from "./theme";

// --- helpers --------------------------------------------------------------

// Pure-white card matching mockup m7: bg-surface-container-lowest rounded-xl p-6 clinical-shadow
const cardStyle = {
  background: "#ffffff",
  borderRadius: "0.75rem",
  boxShadow: "0 4px 20px rgba(0,101,141,0.06)",
  border: "1px solid rgba(189,200,209,0.15)",
};

// Section header with lucide icon and primary color
function SectionHeader({ icon: Icon, children }) {
  return (
    <h2
      className="text-xl font-semibold flex items-center gap-2 px-2 mt-4 mb-2"
      style={{ color: CLINIC_COLORS.primary }}
    >
      <Icon className="w-5 h-5" />
      {children}
    </h2>
  );
}

// Individual question card wrapping YesNoRadio (optional children for follow-up fields)
function QuestionCard({ id, label, description, value, onChange, yesLabel, noLabel, children }) {
  return (
    <div className="p-6" style={cardStyle} data-testid={`question-card-${id}`}>
      <YesNoRadio
        id={id}
        label={label}
        description={description}
        value={value}
        onChange={onChange}
        yesLabel={yesLabel}
        noLabel={noLabel}
      />
      {children && value === "yes" && (
        <div
          className="mt-5 pt-4 pl-4 border-l-2 space-y-3"
          style={{ borderColor: CLINIC_COLORS.primary }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// Styled text input with surface-container-low fill and no border
const filledInputStyle = {
  background: "#f1f4f9",
  border: "1.5px solid transparent",
  transition: "border-color 0.2s, background-color 0.2s",
};
const focusFilledInput = (e, focused) => {
  e.target.style.borderColor = focused ? CLINIC_COLORS.primary : "transparent";
  e.target.style.background = focused ? "#ffffff" : "#f1f4f9";
};

// --- main component -------------------------------------------------------

export const QuestionnaireSection = ({ questionnaire, updateQuestionnaire, takesInterferingMeds }) => {
  const { t } = useTranslation();

  const yn = (key, labelKey, descKey) => (
    <QuestionCard
      id={key}
      label={t(`dashboard.${labelKey || key}`)}
      description={descKey ? t(`dashboard.${descKey}`, "") : undefined}
      value={questionnaire[key]}
      onChange={(v) => updateQuestionnaire(key, v)}
      yesLabel={t("common.yes")}
      noLabel={t("common.no")}
    />
  );

  const isFertileFemale =
    questionnaire.gender === "female" &&
    parseInt(questionnaire.age) >= 15 &&
    parseInt(questionnaire.age) <= 48;

  return (
    <div className="space-y-6">
      {/* Üldandmed - Basic Info card (Age + Gender) */}
      <SectionHeader icon={User}>{t("dashboard.basicInfo")}</SectionHeader>
      <section className="p-6 md:p-8" style={cardStyle}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="age" className="text-sm font-medium" style={{ color: CLINIC_COLORS.muted }}>
              {t("dashboard.age")}
            </Label>
            <Input
              id="age"
              data-testid="input-age"
              type="number"
              min="1"
              max="120"
              placeholder={t("dashboard.enterAge") || "Näiteks: 34"}
              className="h-12 rounded-lg"
              style={filledInputStyle}
              onFocus={(e) => focusFilledInput(e, true)}
              onBlur={(e) => focusFilledInput(e, false)}
              value={questionnaire.age}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");
                updateQuestionnaire("age", value);
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gender" className="text-sm font-medium" style={{ color: CLINIC_COLORS.muted }}>
              {t("dashboard.gender")}
            </Label>
            <div className="flex gap-3 flex-wrap" data-testid="gender-toggle">
              {[
                { value: "male", label: t("dashboard.male") },
                { value: "female", label: t("dashboard.female") },
                { value: "other", label: t("dashboard.other") },
              ].map((opt) => {
                const active = questionnaire.gender === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`gender-${opt.value}`}
                    onClick={() => updateQuestionnaire("gender", opt.value)}
                    className="px-6 py-3 rounded-full border-2 font-semibold text-sm transition-all cursor-pointer select-none"
                    style={active ? {
                      borderColor: "#00658D",
                      background: "rgba(198,231,255,0.4)",
                      color: "#00658D",
                    } : {
                      borderColor: "#dfe3e8",
                      background: "transparent",
                      color: "#3e4850",
                    }}
                    onMouseEnter={(e) => {
                      if (!active) e.currentTarget.style.background = "#f1f4f9";
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Pregnancy (optional) */}
      {isFertileFemale && (
        <QuestionCard
          id="pregnant"
          label={t("dashboard.pregnant")}
          value={questionnaire.pregnant}
          onChange={(v) => updateQuestionnaire("pregnant", v)}
          yesLabel={t("common.yes")}
          noLabel={t("common.no")}
        >
          <div className="space-y-2">
            <Label htmlFor="pregnantWeeks" className="text-sm font-medium" style={{ color: CLINIC_COLORS.muted }}>
              {t("dashboard.pregnantWeeks")}
            </Label>
            <Input
              id="pregnantWeeks"
              data-testid="input-pregnant-weeks"
              type="text"
              placeholder="e.g., 12, 20, 32"
              className="h-12 w-40 rounded-lg"
              style={filledInputStyle}
              onFocus={(e) => focusFilledInput(e, true)}
              onBlur={(e) => focusFilledInput(e, false)}
              value={questionnaire.pregnantWeeks}
              onChange={(e) => updateQuestionnaire("pregnantWeeks", e.target.value)}
            />
          </div>
        </QuestionCard>
      )}

      {/* Health history */}
      <SectionHeader icon={ClipboardList}>{t("dashboard.healthHistory")}</SectionHeader>
      <QuestionCard
        id="thyroidDiseases"
        label={t("dashboard.thyroidDiseases")}
        value={questionnaire.thyroidDiseasesBefore}
        onChange={(v) => updateQuestionnaire("thyroidDiseasesBefore", v)}
        yesLabel={t("common.yes")}
        noLabel={t("common.no")}
      >
        {yn("graves")}
        {yn("hashimoto")}
        {yn("thyroidNodules")}
        {yn("thyroidCancer")}
        {yn("totalThyroidectomy")}
        {yn("partialThyroidectomy")}
      </QuestionCard>

      {/* Medications */}
      <SectionHeader icon={Pill}>{t("dashboard.medications")}</SectionHeader>
      <QuestionCard
        id="levothyroxin"
        label={t("dashboard.levothyroxin")}
        value={questionnaire.levothyroxin}
        onChange={(v) => updateQuestionnaire("levothyroxin", v)}
        yesLabel={t("common.yes")}
        noLabel={t("common.no")}
      >
        <div className="space-y-2">
          <Label htmlFor="levothyroxinDosage" className="text-sm font-medium" style={{ color: CLINIC_COLORS.muted }}>
            {t("dashboard.levothyroxinDosage")}
          </Label>
          <Input
            id="levothyroxinDosage"
            data-testid="input-levothyroxin-dosage"
            type="text"
            placeholder="e.g., 50, 75, 100"
            className="h-12 w-40 rounded-lg"
            style={filledInputStyle}
            onFocus={(e) => focusFilledInput(e, true)}
            onBlur={(e) => focusFilledInput(e, false)}
            value={questionnaire.levothyroxinDosage}
            onChange={(e) => updateQuestionnaire("levothyroxinDosage", e.target.value)}
          />
        </div>
        {yn("levoBeforeBreakfast")}
        {yn("levoRegularly")}
        <p className="text-sm font-medium pt-2" style={{ color: CLINIC_COLORS.muted }}>
          {t("dashboard.interferingMeds")}
        </p>
        {yn("takesCalcium", "calcium")}
        {yn("takesIron", "iron")}
        {yn("takesAcidReducer", "acidReducer")}
        {(questionnaire.takesCalcium === "yes" ||
          questionnaire.takesIron === "yes" ||
          questionnaire.takesAcidReducer === "yes") && (
          <div
            className="mt-2 p-4 rounded-lg"
            style={{ background: "rgba(141,79,0,0.08)", border: "1px solid rgba(141,79,0,0.2)" }}
          >
            <YesNoRadio
              id="levoTwoHoursApart"
              label={t("dashboard.twoHoursApart")}
              value={questionnaire.levoTwoHoursApart}
              onChange={(v) => updateQuestionnaire("levoTwoHoursApart", v)}
              yesLabel={t("common.yes")}
              noLabel={t("common.no")}
            />
          </div>
        )}
      </QuestionCard>

      <QuestionCard
        id="thiamazole"
        label={t("dashboard.thiamazole")}
        value={questionnaire.thiamazole}
        onChange={(v) => updateQuestionnaire("thiamazole", v)}
        yesLabel={t("common.yes")}
        noLabel={t("common.no")}
      >
        <div className="space-y-2">
          <Label htmlFor="thiamazoleDosage" className="text-sm font-medium" style={{ color: CLINIC_COLORS.muted }}>
            {t("dashboard.thiamazoleDosage")}
          </Label>
          <Input
            id="thiamazoleDosage"
            data-testid="input-thiamazole-dosage"
            type="text"
            placeholder="e.g., 5, 10, 20"
            className="h-12 w-40 rounded-lg"
            style={filledInputStyle}
            onFocus={(e) => focusFilledInput(e, true)}
            onBlur={(e) => focusFilledInput(e, false)}
            value={questionnaire.thiamazoleDosage}
            onChange={(e) => updateQuestionnaire("thiamazoleDosage", e.target.value)}
          />
        </div>
      </QuestionCard>

      <QuestionCard
        id="propylthiouracil"
        label={t("dashboard.propylthiouracil")}
        value={questionnaire.propylthiouracil}
        onChange={(v) => updateQuestionnaire("propylthiouracil", v)}
        yesLabel={t("common.yes")}
        noLabel={t("common.no")}
      >
        <div className="space-y-2">
          <Label htmlFor="propylthiouracilDosage" className="text-sm font-medium" style={{ color: CLINIC_COLORS.muted }}>
            {t("dashboard.propylthiouracilDosage")}
          </Label>
          <Input
            id="propylthiouracilDosage"
            data-testid="input-propylthiouracil-dosage"
            type="text"
            placeholder="e.g., 50, 100"
            className="h-12 w-40 rounded-lg"
            style={filledInputStyle}
            onFocus={(e) => focusFilledInput(e, true)}
            onBlur={(e) => focusFilledInput(e, false)}
            value={questionnaire.propylthiouracilDosage}
            onChange={(e) => updateQuestionnaire("propylthiouracilDosage", e.target.value)}
          />
        </div>
      </QuestionCard>

      {yn("cordarone")}
      {yn("heartDisease")}

      {/* Iodine exposure */}
      <SectionHeader icon={FlaskConical}>{t("dashboard.iodineExposure")}</SectionHeader>
      {yn("iodinatedSalt")}
      {yn("iodineSkin")}
      {yn("iodineSpray")}

      {/* Recent imaging */}
      <SectionHeader icon={Scan}>{t("dashboard.recentImaging")}</SectionHeader>
      {yn("ctContrast")}
    </div>
  );
};
